import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import AppHeader from './AppHeader';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    // Verificar se o código é válido
    const verifyCode = async () => {
      if (!oobCode) {
        setStatus('error');
        setMessage('Link inválido ou expirado');
        setIsVerifying(false);
        return;
      }

      try {
        // Verificar o código e obter o email associado
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmail(email);
        setIsVerifying(false);
      } catch (error: any) {
        console.error('Erro ao verificar código:', error);
        setStatus('error');
        
        switch (error.code) {
          case 'auth/expired-action-code':
            setMessage('Este link expirou. Solicite um novo email de recuperação.');
            break;
          case 'auth/invalid-action-code':
            setMessage('Link inválido ou já utilizado.');
            break;
          default:
            setMessage('Erro ao verificar link. Tente solicitar um novo email.');
        }
        setIsVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode]);

  const validatePassword = (password: string): { valid: boolean; error?: string } => {
    if (password.length < 6) {
      return { valid: false, error: 'A senha deve ter pelo menos 6 caracteres' };
    }
    return { valid: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setStatus('error');
      setMessage('Por favor, preencha todos os campos');
      return;
    }

    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('As senhas não coincidem');
      return;
    }

    const validation = validatePassword(password);
    if (!validation.valid) {
      setStatus('error');
      setMessage(validation.error || 'Senha inválida');
      return;
    }

    if (!oobCode) {
      setStatus('error');
      setMessage('Código de recuperação inválido');
      return;
    }

    setIsLoading(true);
    setStatus('idle');
    setMessage('');

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('success');
      setMessage('Senha alterada com sucesso! Redirecionando para o login...');
      
      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      console.error('Erro ao redefinir senha:', error);
      setStatus('error');
      
      switch (error.code) {
        case 'auth/expired-action-code':
          setMessage('Este link expirou. Solicite um novo email de recuperação.');
          break;
        case 'auth/invalid-action-code':
          setMessage('Link inválido ou já utilizado.');
          break;
        case 'auth/weak-password':
          setMessage('Senha muito fraca. Use pelo menos 6 caracteres.');
          break;
        default:
          setMessage('Erro ao redefinir senha. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2B5A41] to-[#203C2E] flex flex-col">
        <AppHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Verificando link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2B5A41] to-[#203C2E] flex flex-col">
      <AppHeader />
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2B5A41] bg-opacity-10 rounded-full mb-4">
                <Lock className="w-8 h-8 text-[#2B5A41]" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Nova Senha
              </h1>
              {email && (
                <p className="text-gray-600 text-sm">
                  Redefinindo senha para <span className="font-medium">{email}</span>
                </p>
              )}
            </div>

            {/* Mensagens de status */}
            {status === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{message}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{message}</p>
                  {(message.includes('expirou') || message.includes('inválido')) && (
                    <Link
                      to="/esqueci-senha"
                      className="text-sm text-red-600 hover:text-red-700 font-medium underline mt-2 inline-block"
                    >
                      Solicitar novo email
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Formulário */}
            {!status || status === 'idle' || status === 'error' ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-600 mb-2">
                    Nova Senha
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <Lock className="w-5 h-5" />
                    </span>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                      disabled={isLoading}
                      className="w-full pl-10 pr-12 py-3 bg-[#E8E8E8] rounded-full border-2 border-transparent focus:border-[#2B5A41] focus:bg-white outline-none transition text-gray-700 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-600 mb-2">
                    Confirmar Nova Senha
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <Lock className="w-5 h-5" />
                    </span>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Digite a senha novamente"
                      required
                      disabled={isLoading}
                      className="w-full pl-10 pr-12 py-3 bg-[#E8E8E8] rounded-full border-2 border-transparent focus:border-[#2B5A41] focus:bg-white outline-none transition text-gray-700 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-[#2B5A41] text-white rounded-full font-medium hover:bg-[#203C2E] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Alterando senha...' : 'Alterar Senha'}
                </button>
              </form>
            ) : null}

            {/* Link para voltar ao login */}
            {status !== 'success' && (
              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-[#2B5A41] hover:text-[#203C2E] font-medium transition"
                >
                  Voltar para o login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
